import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/auth";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import ChatPage from "@/pages/ChatPage";
import { BackofficeListPage, BackofficeCasePage } from "@/pages/BackofficePage";
import BackofficeKBPage from "@/pages/BackofficeKBPage";
import BackofficeEscalationsPage from "@/pages/BackofficeEscalationsPage";
import BackofficeMetricsPage from "@/pages/BackofficeMetricsPage";
import { BackofficeUsersListPage, BackofficeUserDetailPage } from "@/pages/BackofficeUsersPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import InvitePage from "@/pages/InvitePage";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ next: location.pathname }} />;
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  // CRITICAL: detect session_id in URL fragment synchronously during render,
  // BEFORE other routes/auth checks run. This prevents the AuthProvider's
  // /auth/me check from racing the OAuth callback.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/change-password" element={<Protected><ChangePasswordPage /></Protected>} />
      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/cases/:caseId" element={<Protected><ChatPage /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><BackofficeListPage /></Protected>} />
      <Route path="/admin/cases/:caseId" element={<Protected adminOnly><BackofficeCasePage /></Protected>} />
      <Route path="/admin/kb" element={<Protected adminOnly><BackofficeKBPage /></Protected>} />
      <Route path="/admin/escalations" element={<Protected adminOnly><BackofficeEscalationsPage /></Protected>} />
      <Route path="/admin/metrics" element={<Protected adminOnly><BackofficeMetricsPage /></Protected>} />
      <Route path="/admin/users" element={<Protected adminOnly><BackofficeUsersListPage /></Protected>} />
      <Route path="/admin/users/:userId" element={<Protected adminOnly><BackofficeUserDetailPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
